// FileTransfer Component - File sharing with confirmation and progress
// Based on documentation from docs/05_FILE_SHARING_SYSTEM.md

import React, { useState, useEffect, useRef } from 'react';
import { 
  getFileExt, 
  formatFileSize, 
  adjustFileName,
  getImageSrc 
} from '../utils/helpers';
import { showToast } from '../utils/notifications';
import { IMAGE_TYPE, VIDEO_TYPE, MAX_FILE_SIZE } from '../utils/constants';
import '../styles/FileTransfer.css';

const FileTransfer = ({ chatManager, currentUser, onFileMessage }) => {
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sendingFiles, setSendingFiles] = useState([]);
  const [receivingFiles, setReceivingFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  
  // Use ref to persist chunks and metadata across renders
  const receivedChunksRef = useRef(new Map()); // id -> chunks[]
  const fileMetadataRef = useRef(new Map()); // id -> file metadata
  const dragCounterRef = useRef(0); // Track drag enter/leave events
  const sentFileMessagesRef = useRef(new Set()); // Track which files we've added to chat
  const sendingFilesRef = useRef(new Map()); // Track sending files metadata
  const receivingFilesRef = useRef(new Map()); // Track receiving files metadata

  useEffect(() => {
    if (!chatManager) return;

    // Listen for file metadata (confirmation request)
    const handleFileMeta = ({ peerId, data }) => {
      const fileData = data.data;
      const sender = data.user;
      
      console.log('[FileTransfer] Received file metadata:', fileData);
      
      const fileInfo = {
        id: fileData.id,
        name: fileData.name,
        size: fileData.size,
        type: fileData.type,
        from: peerId,
        sender: sender
      };
      
      // Store metadata in ref for later assembly
      fileMetadataRef.current.set(fileData.id, fileInfo);
      
      setPendingFiles(prev => [...prev, fileInfo]);

      // Auto-accept files - direct download with progress bar
      console.log('[FileTransfer] Auto-accepting file:', fileData.name);
      handleAcceptFile(peerId, fileData.id);
      showToast(`Receiving: ${fileData.name} (${formatFileSize(fileData.size)})`, 'info');
    };

    // Listen for file chunks
    const handleFileChunk = ({ peerId, id, seq, chunk }) => {
      console.log('[FileTransfer] Received chunk:', { id, seq, size: chunk?.byteLength || 0 });
      
      if (!receivedChunksRef.current.has(id)) {
        receivedChunksRef.current.set(id, []);
      }
      receivedChunksRef.current.get(id).push({ seq, chunk });
    };

    // Listen for file completion
    const handleFileReceived = ({ id, peerId }) => {
      console.log('[FileTransfer] File transfer completed:', id, 'from peer:', peerId);
      
      const chunks = receivedChunksRef.current.get(id) || [];
      // Get metadata from ref (avoids stale closure issues)
      const fileMetadata = fileMetadataRef.current.get(id);
      
      console.log('[FileTransfer] Assembling file:', { 
        id, 
        chunksReceived: chunks.length, 
        fileMetadata: fileMetadata?.name,
        hasFrom: !!fileMetadata?.from
      });
      
      if (chunks.length > 0 && fileMetadata) {
        // Sort chunks by sequence and combine
        chunks.sort((a, b) => a.seq - b.seq);
        const combined = new Blob(chunks.map(c => c.chunk), { type: fileMetadata.type });
        
        console.log('[FileTransfer] Created blob:', { size: combined.size, type: combined.type });
        
        // Create blob URL
        const blobUrl = URL.createObjectURL(combined);
        
        const completedFile = {
          ...fileMetadata,
          blobUrl,
          timestamp: Date.now(),
          status: 'completed'
        };
        
        console.log('[FileTransfer] Calling onFileMessage with:', completedFile);
        
        // Add to chat messages
        if (onFileMessage) {
          onFileMessage(completedFile);
        }
        
        setReceivingFiles(prev => prev.filter(f => f.id !== id));
        setPendingFiles(prev => prev.filter(f => f.id !== id));
        receivedChunksRef.current.delete(id);
        fileMetadataRef.current.delete(id);
        receivingFilesRef.current.delete(id);
        
        showToast(`File received: ${fileMetadata.name}`, 'success');
      } else {
        console.error('[FileTransfer] Failed to assemble file - missing chunks or metadata', {
          hasChunks: chunks.length > 0,
          hasMetadata: !!fileMetadata,
          chunksDetail: chunks.length > 0 ? `First chunk size: ${chunks[0]?.chunk?.byteLength || 0}` : 'No chunks',
          allMetadataKeys: Array.from(fileMetadataRef.current.keys()),
          allChunkKeys: Array.from(receivedChunksRef.current.keys())
        });
      }
    };

    // Listen for send progress
    const handleSendProgress = ({ id, sent, total, percent }) => {
      const file = sendingFilesRef.current.get(id);
      const startTime = file?.startTime || Date.now();
      const elapsedSec = (Date.now() - startTime) / 1000;
      const speed = elapsedSec > 0 ? sent / elapsedSec : 0;
      const remaining = total - sent;
      const etaSeconds = speed > 0 ? remaining / speed : 0;
      setSendingFiles(prev => prev.map(f => 
        f.id === id ? { ...f, sent, total, percent, speed, etaSeconds } : f
      ));
      // Update chat message with progress using ref
      if (onFileMessage) {
        if (file) {
          onFileMessage({ ...file, sent, total, percent, speed, etaSeconds, status: 'sending' });
        }
      }
    };

    // Listen for receive progress
    const handleReceiveProgress = ({ id, received, total, percent }) => {
      setReceivingFiles(prev => {
        const existing = prev.find(f => f.id === id);
        const startTime = existing?.startTime || Date.now();
        const elapsedSec = (Date.now() - startTime) / 1000;
        const speed = elapsedSec > 0 ? received / elapsedSec : 0;
        const remaining = total - received;
        const etaSeconds = speed > 0 ? remaining / speed : 0;
        if (existing) {
          const updatedFile = { ...existing, received, total, percent, speed, etaSeconds };
          receivingFilesRef.current.set(id, updatedFile);
          // Update chat message with progress
          if (onFileMessage) {
            onFileMessage({ ...updatedFile, status: 'receiving' });
          }
          return prev.map(f => f.id === id ? updatedFile : f);
        } else {
          const pending = pendingFiles.find(f => f.id === id);
          if (pending) {
            const receivingFile = { ...pending, received, total, percent, speed, etaSeconds, status: 'receiving', startTime: Date.now() };
            receivingFilesRef.current.set(id, receivingFile);
            // Add to chat when receiving starts (only once)
            if (onFileMessage && !sentFileMessagesRef.current.has(id)) {
              sentFileMessagesRef.current.add(id);
              onFileMessage(receivingFile);
            }
            return [...prev, receivingFile];
          }
        }
        return prev;
      });
    };

    // Handle send paused (data channel dropped mid-transfer)
    const handleSendPaused = ({ id, peerId, offset, name, size }) => {
      console.log('[FileTransfer] File send paused:', { id, offset, name });
      setSendingFiles(prev => prev.map(f =>
        f.id === id ? { ...f, status: 'paused', pausedOffset: offset } : f
      ));
      if (onFileMessage) {
        onFileMessage({ id, name, size, status: 'paused', percent: size ? Math.round((offset / size) * 100) : 0, sent: offset, total: size });
      }
      showToast(`Transfer paused: ${name} — will resume automatically`, 'info');
    };

    // Handle send resumed
    const handleSendResumed = ({ id, peerId, offset, name, size }) => {
      console.log('[FileTransfer] File send resumed:', { id, offset, name });
      setSendingFiles(prev => prev.map(f =>
        f.id === id ? { ...f, status: 'sending', pausedOffset: null } : f
      ));
      if (onFileMessage) {
        onFileMessage({ id, name, size, status: 'sending', sent: offset, total: size, percent: size ? Math.round((offset / size) * 100) : 0 });
      }
      showToast(`Resuming: ${name} from ${formatFileSize(offset)}`, 'info');
    };

    // Handle receive resumed (sender notified us they're resuming from offset)
    const handleReceiveResumed = ({ id, peerId, offset, name, size }) => {
      console.log('[FileTransfer] File receive resumed:', { id, offset, name });
      // Clear previously received chunks since we're starting from a specific offset
      receivedChunksRef.current.set(id, []);
      setReceivingFiles(prev => prev.map(f =>
        f.id === id ? { ...f, status: 'receiving', received: offset, percent: size ? Math.round((offset / size) * 100) : 0 } : f
      ));
      if (onFileMessage) {
        onFileMessage({ id, name, size, status: 'receiving', received: offset, total: size, percent: size ? Math.round((offset / size) * 100) : 0 });
      }
      showToast(`Resuming receive: ${name} from ${formatFileSize(offset)}`, 'info');
    };

    // Handle send completed
    const handleSendCompleted = ({ id, peerId }) => {
      setSendingFiles(prev => prev.filter(f => f.id !== id));
      sendingFilesRef.current.delete(id);
    };

    const off1 = chatManager.on('file:meta', handleFileMeta);
    const off2 = chatManager.on('file:chunk', handleFileChunk);
    const off3 = chatManager.on('file:received', handleFileReceived);
    const off4 = chatManager.on('file:send:progress', handleSendProgress);
    const off5 = chatManager.on('file:receive:progress', handleReceiveProgress);
    const off6 = chatManager.on('file:send:paused', handleSendPaused);
    const off7 = chatManager.on('file:send:resumed', handleSendResumed);
    const off8 = chatManager.on('file:receive:resumed', handleReceiveResumed);
    const off9 = chatManager.on('file:send:completed', handleSendCompleted);

    return () => {
      off1();
      off2();
      off3();
      off4();
      off5();
      off6();
      off7();
      off8();
      off9();
    };
  }, [chatManager]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAcceptFile = (peerId, fileId) => {
    const file = pendingFiles.find(f => f.id === fileId);
    if (file) {
      // Add to receiving list
      setReceivingFiles(prev => [...prev, {
        ...file,
        received: 0,
        percent: 0
      }]);
    }
    chatManager.acceptFile(peerId, fileId);
    setPendingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        showToast(`File too large: ${file.name} (max ${formatFileSize(MAX_FILE_SIZE)})`, 'error');
        continue;
      }

      // Adjust filename
      const adjustedFile = adjustFileName(file);
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      // Create blob URL for preview
      const blobUrl = URL.createObjectURL(adjustedFile);
      
      // Add to sending list
      const sendingFile = {
        id,
        name: adjustedFile.name,
        size: adjustedFile.size,
        type: adjustedFile.type,
        blobUrl,
        sent: 0,
        total: adjustedFile.size,
        percent: 0,
        status: 'sending',
        startTime: Date.now(),
        speed: 0,
        etaSeconds: 0
      };
      
      // Store in ref for progress updates
      sendingFilesRef.current.set(id, sendingFile);
      
      setSendingFiles(prev => [...prev, sendingFile]);
      
      // Add to chat immediately
      if (onFileMessage && !sentFileMessagesRef.current.has(id)) {
        sentFileMessagesRef.current.add(id);
        onFileMessage(sendingFile);
      }

      // Send file
      try {
        await chatManager.sendFile(adjustedFile, { id });
        
        // Update to completed in chat
        if (onFileMessage) {
          onFileMessage({ ...sendingFile, status: 'completed', percent: 100, blobUrl });
        }
        
        // Clean up
        sendingFilesRef.current.delete(id);
        setSendingFiles(prev => prev.filter(f => f.id !== id));
        showToast(`File sent: ${adjustedFile.name}`, 'success');
      } catch (error) {
        console.error('Error sending file:', error);
        showToast(`Error sending file: ${adjustedFile.name}`, 'error');
        sendingFilesRef.current.delete(id);
        setSendingFiles(prev => prev.filter(f => f.id !== id));
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [chatManager]); // eslint-disable-line react-hooks/exhaustive-deps

  // Global drag-drop detection for window
  useEffect(() => {
    const handleWindowDragEnter = (e) => {
      e.preventDefault();
      dragCounterRef.current++;
      if (e.dataTransfer.types.includes('Files')) {
        setDragOver(true);
      }
    };

    const handleWindowDragLeave = (e) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setDragOver(false);
      }
    };

    const handleWindowDragOver = (e) => {
      e.preventDefault();
    };

    const handleWindowDrop = (e) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setDragOver(false);
    };

    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragenter', handleWindowDragEnter);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, []);

  const renderFileIcon = (file) => {
    const ext = getFileExt(file.name);
    const imageSrc = getImageSrc(file.name, file.blobUrl);
    
    if (IMAGE_TYPE.includes(ext) && file.blobUrl) {
      return <img src={file.blobUrl} alt={file.name} className="file-preview-img" />;
    } else if (VIDEO_TYPE.includes(ext) && file.blobUrl) {
      return <video src={file.blobUrl} className="file-preview-video" controls={false} />;
    } else {
      return <img src={imageSrc} alt={ext} className="file-icon" />;
    }
  };

  return (
    <div className="file-transfer-container">
      {/* Drop Zone */}
      <div 
        className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload-input"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            handleFileSelect(Array.from(e.target.files));
            e.target.value = ''; // Reset to allow same file selection
          }}
        />
        <label htmlFor="file-upload-input" className="file-drop-label">
          <i className="fas fa-cloud-upload-alt"></i>
          <span>Drop files here or click to browse</span>
        </label>
      </div>

      {/* Sending Files */}
      {sendingFiles.length > 0 && (
        <div className="file-list">
          <h4>Sending Files</h4>
          {sendingFiles.map(file => (
            <div key={file.id} className={`file-item ${file.status === 'paused' ? 'paused' : 'sending'}`}>
              {renderFileIcon(file)}
              <div className="file-info">
                <div className="file-name">{file.name}</div>
                <div className="file-size">{formatFileSize(file.size)}</div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${file.percent}%` }}></div>
                </div>
                <div className="progress-text">
                  {file.status === 'paused' ? (
                    <span className="file-paused-text">
                      <i className="fas fa-pause-circle"></i> Paused at {file.percent}% — auto-resuming…
                    </span>
                  ) : (
                    `${file.percent}%`
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Receiving Files */}
      {receivingFiles.length > 0 && (
        <div className="file-list">
          <h4>Receiving Files</h4>
          {receivingFiles.map(file => (
            <div key={file.id} className={`file-item ${file.status === 'paused' ? 'paused' : 'receiving'}`}>
              {renderFileIcon(file)}
              <div className="file-info">
                <div className="file-name">{file.name}</div>
                <div className="file-size">{formatFileSize(file.size)}</div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${file.percent || 0}%` }}></div>
                </div>
                <div className="progress-text">
                  {file.status === 'paused' ? (
                    <span className="file-paused-text">
                      <i className="fas fa-pause-circle"></i> Paused at {file.percent || 0}% — auto-resuming…
                    </span>
                  ) : (
                    `${file.percent || 0}%`
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Completed Files - Hidden as requested */}
    </div>
  );
};

export default FileTransfer;
